import React, { useState, useEffect, useContext, useRef } from "react";
import { AuthContext } from "../../context/AuthContext";
import api from "../../utils/Api";

const PER_PAGE = 12;

const estadoBadge = {
  pendiente: "tn-badge tn-badge--yellow",
  resuelta:  "tn-badge tn-badge--green",
  rechazada: "tn-badge tn-badge--red",
};

export default function IncidenciasAdmin() {
  const { token, loading } = useContext(AuthContext);
  const [incidencias, setIncidencias] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filterEstado, setFilterEstado] = useState("pendiente");
  const [generalError, setGeneralError] = useState("");
  const [toast, setToast] = useState({ show: false, message: "" });
  const [loadingId, setLoadingId] = useState(null);
  const pendingRef = useRef(false);

  const [modalOpen, setModalOpen]     = useState(false);
  const [empleados, setEmpleados]     = useState([]);
  const [fichajesEmpleado, setFichajesEmpleado] = useState([]);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm]               = useState({ usuario_id: "", tipo: "falta", comentario: "", fecha_hora: today, fichaje_id: "" });
  const [formError, setFormError]     = useState("");
  const [formLoading, setFormLoading] = useState(false);

  const showToast = (message) => {
    setToast({ show: true, message });
    setTimeout(() => setToast({ show: false, message: "" }), 4000);
  };

  const formatFechaHora = (str) => {
    if (!str) return "";
    const dt = new Date(str.replace(" ", "T"));
    return isNaN(dt) ? str : dt.toLocaleString("es-ES");
  };

  const loadIncidencias = async (currentPage = page, estado = filterEstado) => {
    if (!token) return;
    try {
      const params = { page: currentPage, per_page: PER_PAGE };
      if (estado) params.estado = estado;
      const res = await api.get("/incidencias", { params });
      setIncidencias(res.data.data);
      setTotal(res.data.total);
    } catch {
      setGeneralError("Error al cargar las incidencias.");
    }
  };

  useEffect(() => { loadIncidencias(page, filterEstado); }, [token, page, filterEstado]);

  const handleFiltroEstado = (e) => { setFilterEstado(e.target.value); setPage(1); };

  const handleCambiarEstado = async (id, nuevoEstado) => {
    if (!token || pendingRef.current) return;
    pendingRef.current = true;
    setLoadingId(id);
    try {
      await api.put(`/incidencias/${id}`, { estado: nuevoEstado });
      showToast(`Estado cambiado a "${nuevoEstado}"`);
      loadIncidencias(page, filterEstado);
    } catch (err) {
      setGeneralError(err.response?.data?.error || "Error al cambiar el estado.");
    } finally {
      pendingRef.current = false;
      setLoadingId(null);
    }
  };

  const openModal = async () => {
    setFormError("");
    setFichajesEmpleado([]);
    setForm({ usuario_id: "", tipo: "falta", comentario: "", fecha_hora: today, fichaje_id: "" });
    try {
      const res = await api.get("/usuarios");
      setEmpleados(res.data.filter(u => u.rol !== "admin"));
    } catch {
      setEmpleados([]);
    }
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handleFormChange = async (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value, ...(name === "usuario_id" ? { fichaje_id: "" } : {}) }));
    if (name === "usuario_id" && value) {
      try {
        const res = await api.get("/fichajes", { params: { usuario_id: value, per_page: 100 } });
        setFichajesEmpleado(res.data.data || []);
      } catch {
        setFichajesEmpleado([]);
      }
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!form.usuario_id) { setFormError("Selecciona un empleado."); return; }
    if (!form.comentario.trim()) { setFormError("El comentario es obligatorio."); return; }
    setFormLoading(true);
    setFormError("");
    try {
      await api.post("/incidencias", {
        usuario_id: Number(form.usuario_id),
        tipo:       form.tipo,
        comentario: form.comentario.trim(),
        fecha_hora: form.fecha_hora || undefined,
        fichaje_id: form.fichaje_id ? Number(form.fichaje_id) : undefined,
      });
      closeModal();
      showToast("Incidencia creada correctamente.");
      loadIncidencias(page, filterEstado);
    } catch (err) {
      setFormError(err.response?.data?.message || "Error al crear la incidencia.");
    } finally {
      setFormLoading(false);
    }
  };

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: "var(--clr-muted)", fontSize: 14 }}>
      Cargando…
    </div>
  );

  const totalPages = Math.ceil(total / PER_PAGE);

  return (
    <div>
      {toast.show && <div className="tn-toast tn-toast--success">{toast.message}</div>}

      <h1 className="tn-page-title">Incidencias</h1>
      <p className="tn-page-sub">Gestiona las incidencias reportadas por los empleados</p>

      {generalError && (
        <div className="tn-alert tn-alert--danger" style={{ marginBottom: 16 }}>{generalError}</div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <label className="tn-label" style={{ margin: 0, whiteSpace: "nowrap" }}>Filtrar por estado</label>
        <select className="tn-input tn-select" style={{ width: "auto" }} value={filterEstado} onChange={handleFiltroEstado}>
          <option value="">Todos</option>
          <option value="pendiente">Pendiente</option>
          <option value="resuelta">Resuelta</option>
          <option value="rechazada">Rechazada</option>
        </select>
        <span style={{ fontSize: 13, color: "var(--clr-muted)" }}>{total} resultado{total !== 1 ? "s" : ""}</span>
        <button
          className="tn-btn tn-btn--primary"
          style={{ marginLeft: "auto", fontSize: 13 }}
          onClick={openModal}
        >
          + Nueva incidencia
        </button>
      </div>

      {incidencias.length > 0 ? (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 14, marginBottom: 20 }}>
            {incidencias.map((i) => (
              <div key={i.id} className="tn-card tn-card--hover" style={{ padding: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, color: "var(--clr-muted)" }}>{i.tipo}</span>
                  <span className={estadoBadge[i.estado] || "tn-badge tn-badge--slate"}>{i.estado}</span>
                </div>
                <p style={{ fontSize: 14, fontWeight: 600, color: "var(--clr-text)", margin: "0 0 2px" }}>{i.usuario?.nombre || "—"}</p>
                <p style={{ fontSize: 12.5, color: "var(--clr-muted-lt)", margin: "0 0 8px" }}>{formatFechaHora(i.fecha_hora)}</p>
                <p style={{ fontSize: 13, color: "var(--clr-muted)", margin: "0 0 12px", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical" }}>{i.comentario}</p>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {i.estado !== "pendiente" && (
                    <button className="tn-btn tn-btn--ghost" style={{ fontSize: 12, padding: "4px 10px" }} disabled={loadingId !== null} onClick={() => handleCambiarEstado(i.id, "pendiente")}>Pendiente</button>
                  )}
                  {i.estado !== "resuelta" && (
                    <button className="tn-btn tn-btn--success" style={{ fontSize: 12, padding: "4px 10px" }} disabled={loadingId !== null} onClick={() => handleCambiarEstado(i.id, "resuelta")}>Resuelta</button>
                  )}
                  {i.estado !== "rechazada" && (
                    <button className="tn-btn tn-btn--danger" style={{ fontSize: 12, padding: "4px 10px" }} disabled={loadingId !== null} onClick={() => handleCambiarEstado(i.id, "rechazada")}>Rechazada</button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="tn-pagination">
              <button className="tn-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
              {[...Array(totalPages)].map((_, i) => (
                <button key={i} className={`tn-page-btn${page === i + 1 ? " active" : ""}`} onClick={() => setPage(i + 1)}>{i + 1}</button>
              ))}
              <button className="tn-page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          )}
        </>
      ) : (
        <div className="tn-card" style={{ padding: "60px 20px", textAlign: "center", color: "var(--clr-muted)", fontSize: 14 }}>
          No hay incidencias para mostrar
        </div>
      )}
      {modalOpen && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,.45)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div
            className="tn-card"
            style={{ width: "100%", maxWidth: 420, padding: 24, position: "relative" }}
          >
            <button
              onClick={closeModal}
              style={{
                position: "absolute", top: 12, right: 14, background: "none",
                border: "none", fontSize: 18, cursor: "pointer", color: "var(--clr-muted)",
              }}
            >✕</button>

            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Nueva incidencia</h2>

            <form onSubmit={handleFormSubmit}>
              <div className="tn-form-group" style={{ marginBottom: 14 }}>
                <label className="tn-label">Empleado</label>
                <select
                  className="tn-input tn-select"
                  name="usuario_id"
                  value={form.usuario_id}
                  onChange={handleFormChange}
                  required
                >
                  <option value="">Selecciona un empleado…</option>
                  {empleados.map(e => (
                    <option key={e.id} value={e.id}>{e.nombre}</option>
                  ))}
                </select>
              </div>

              <div className="tn-form-group" style={{ marginBottom: 14 }}>
                <label className="tn-label">Tipo</label>
                <select
                  className="tn-input tn-select"
                  name="tipo"
                  value={form.tipo}
                  onChange={handleFormChange}
                >
                  <option value="falta">Falta</option>
                  <option value="retraso">Retraso</option>
                  <option value="incidencia">Incidencia</option>
                </select>
              </div>

              <div className="tn-form-group" style={{ marginBottom: 14 }}>
                <label className="tn-label">Fecha</label>
                <input
                  type="date"
                  className="tn-input"
                  name="fecha_hora"
                  value={form.fecha_hora}
                  onChange={handleFormChange}
                  required
                />
              </div>

              {fichajesEmpleado.length > 0 && (
                <div className="tn-form-group" style={{ marginBottom: 14 }}>
                  <label className="tn-label">Fichaje asociado (opcional)</label>
                  <select
                    className="tn-input tn-select"
                    name="fichaje_id"
                    value={form.fichaje_id}
                    onChange={handleFormChange}
                  >
                    <option value="">— No asociar —</option>
                    {fichajesEmpleado.map(f => (
                      <option key={f.id} value={f.id}>
                        {f.tipo} — {f.fecha} {f.hora_entrada || f.hora_salida || f.hora_entrada_descanso || ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="tn-form-group" style={{ marginBottom: 14 }}>
                <label className="tn-label">Comentario</label>
                <textarea
                  className="tn-input"
                  name="comentario"
                  value={form.comentario}
                  onChange={handleFormChange}
                  rows={3}
                  maxLength={255}
                  required
                  style={{ resize: "vertical" }}
                />
              </div>

              {formError && (
                <div className="tn-alert tn-alert--danger" style={{ marginBottom: 10 }}>
                  {formError}
                </div>
              )}

              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button type="button" className="tn-btn tn-btn--ghost" onClick={closeModal}>
                  Cancelar
                </button>
                <button type="submit" className="tn-btn tn-btn--primary" disabled={formLoading}>
                  {formLoading ? "Guardando…" : "Crear incidencia"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
